// VideoAd.tsx
import React, { useRef, useEffect, useState } from 'react';
import './VideoAd.css';
import { openPopup } from '../../../stateManagement/actions/popupActions';

interface VideoAdProps {
  title: string;
  subtitle: string;
  buttonText: string;
  videoThumbnail: string;
  brand: string;
  logo?: string;
  videoUrl?: string;
}

const VideoAd: React.FC<VideoAdProps> = ({
  title,
  subtitle,
  buttonText,
  videoThumbnail,
  brand,
  logo,
  videoUrl
}) => {
  const videoRef = useRef<HTMLIFrameElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string | null => {
    if (!url || typeof url !== 'string') return null;
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const videoId = videoUrl ? getYouTubeVideoId(videoUrl) : null;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && videoId && !isPlaying) {
            // Auto-play when component comes into view
            setShowVideo(true);
            setIsPlaying(true);
          } else if (!entry.isIntersecting && isPlaying) {
            // Pause when component goes out of view
            setIsPlaying(false);
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of the component is visible
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [videoId, isPlaying]);

  const handleVideoClick = () => {
    if (videoId && !iframeError) {
      setIframeError(false);
      setShowVideo(true);
      setIsPlaying(true);
    } else {

    }
  };

  const handleWatchNowClick = () => {
    if (videoUrl) {
      // Open the video in a new tab
      const newWindow = window.open(videoUrl, '_blank', 'noopener,noreferrer');
    } else {
      openPopup('Alert', 'No video URL available');
    }
  };

  const truncateString = (str: string, maxLength: number) => {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength) + '...';
  };

  return (
    <div className="video-ad-container" ref={containerRef}>
      <div className="video-ad-content">
        <div className="video-ad-left">
          <div className="video-ad-thumbnail" onClick={handleVideoClick}>
            {showVideo && videoId && !iframeError ? (
              <iframe
                ref={videoRef}
                src={`https://www.youtube.com/embed/${videoId}?autoplay=${isPlaying ? 1 : 0}&mute=1&rel=0&modestbranding=1`}
                title={`${brand || 'Video'} video`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onError={() => {
                  setIframeError(true);
                  setShowVideo(false);
                  setIsPlaying(false);
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            ) : (
              <>
                <img src={videoThumbnail} alt={`${brand || ''} video`} />
                <div className="video-ad-play-button">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M8 5v14l11-7z" fill="currentColor"/>
                  </svg>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="video-ad-right">
          <h3 className="video-ad-title" title={title}>{truncateString(title, 40) || ''}</h3>
          <p className="video-ad-subtitle" title={subtitle}>{truncateString(subtitle, 80) || ''}</p>
          <button className="video-ad-button" onClick={handleWatchNowClick}>
            {buttonText || ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoAd;